import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'
import EmsLogo from '@/components/EmsLogo'
import { getCollegeByAdminEmail } from '@/utils/userAccess'

interface SessionSubjectRow {
  id?: number
  student_group?: string
  subject_name?: string
  college_eiin: number | string
}

export default function SessionSubjectPage() {
  const navigate = useNavigate()
  const [subjects, setSubjects] = useState<SessionSubjectRow[]>([])
  const [loading, setLoading] = useState(true)
  const [collegeEiin, setCollegeEiin] = useState<number | string | null>(null)
  const [collegeName, setCollegeName] = useState<string>('')
  const [statusMsg, setStatusMsg] = useState('')

  // Form / Modal State
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingRow, setEditingRow] = useState<SessionSubjectRow | null>(null)
  const [formData, setFormData] = useState({
    student_group: 'Science',
    subject_name: ''
  })
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Load college and subjects
  const loadData = async () => {
    setLoading(true)
    setErrorMsg(null)
    try {
      const { data: authData } = await supabase.auth.getUser()
      const user = authData.user
      if (!user) {
        navigate('/login', { replace: true })
        return
      }

      const college = await getCollegeByAdminEmail(user.email)
      if (!college) {
        setStatusMsg('College profile not found for your admin email')
        setLoading(false)
        return
      }

      setCollegeEiin(college.college_eiin)
      setCollegeName(college.college_name)

      // Fetch session_subject for this college
      const { data, error } = await supabase
        .from('session_subject')
        .select('*')
        .eq('college_eiin', college.college_eiin)
        .order('id', { ascending: true })

      if (error) throw error
      setSubjects(data || [])
      setStatusMsg(`Total ${data?.length || 0} subjects configured`)
    } catch (err: any) {
      console.error('SessionSubjectPage load error:', err)
      setErrorMsg(err?.message || 'Failed to load session subjects')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleOpenAdd = () => {
    setEditingRow(null)
    setFormData({ student_group: 'Science', subject_name: '' })
    setErrorMsg(null)
    setShowAddModal(true)
  }

  const handleOpenEdit = (row: SessionSubjectRow) => {
    setEditingRow(row)
    setFormData({
      student_group: row.student_group || 'Science',
      subject_name: row.subject_name || ''
    })
    setErrorMsg(null)
    setShowAddModal(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!collegeEiin) {
      setErrorMsg('College EIIN missing')
      return
    }

    if (!formData.subject_name.trim()) {
      setErrorMsg('Please enter a subject name')
      return
    }

    setSaving(true)
    setErrorMsg(null)

    try {
      if (editingRow && editingRow.id) {
        // Update
        const { error } = await supabase
          .from('session_subject')
          .update({
            student_group: formData.student_group.trim(),
            subject_name: formData.subject_name.trim()
          })
          .eq('id', editingRow.id)
          .eq('college_eiin', collegeEiin)

        if (error) throw error
      } else {
        // Insert new subject
        const { error } = await supabase
          .from('session_subject')
          .insert([
            {
              student_group: formData.student_group.trim(),
              subject_name: formData.subject_name.trim(),
              college_eiin: collegeEiin
            }
          ])

        if (error) throw error
      }

      setShowAddModal(false)
      await loadData()
    } catch (err: any) {
      console.error('handleSave subject error:', err)
      setErrorMsg(err?.message || 'Failed to save subject')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id?: number) => {
    if (!id || !collegeEiin) return
    if (!confirm('Are you sure you want to delete this subject?')) return

    try {
      const { error } = await supabase
        .from('session_subject')
        .delete()
        .eq('id', id)
        .eq('college_eiin', collegeEiin)

      if (error) throw error
      await loadData()
    } catch (err: any) {
      alert('Delete error: ' + (err?.message || 'Failed to delete'))
    }
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <EmsLogo size={34} />
          <button onClick={() => navigate('/dashboard')} style={styles.btnBack}>
            ← Dashboard
          </button>
          <div>
            <h1 style={styles.title}>Session Subjects Management</h1>
            <p style={styles.subtitle}>{collegeName} (EIIN: {collegeEiin})</p>
          </div>
        </div>

        <button onClick={handleOpenAdd} style={styles.btnAdd}>
          + Add New Subject
        </button>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        {errorMsg && <div style={styles.errorBox}>{errorMsg}</div>}

        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800 }}>Subject List</h2>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>{statusMsg}</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '60px', textAlign: 'center' }}>#</th>
                  <th style={styles.th}>Group</th>
                  <th style={styles.th}>Subject Name</th>
                  <th style={{ ...styles.th, width: '120px', textAlign: 'center' }}>College EIIN</th>
                  <th style={{ ...styles.th, width: '140px', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      Loading subjects...
                    </td>
                  </tr>
                ) : subjects.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      No subjects configured yet. Click <strong>+ Add New Subject</strong> above to add one.
                    </td>
                  </tr>
                ) : (
                  subjects.map((sub, idx) => (
                    <tr key={sub.id || idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700 }}>{idx + 1}</td>
                      <td style={styles.td}>
                        <span style={styles.badgeGroup}>{sub.student_group || 'General'}</span>
                      </td>
                      <td style={{ ...styles.td, fontWeight: 700, color: '#0f172a' }}>{sub.subject_name}</td>
                      <td style={{ ...styles.td, textAlign: 'center', fontSize: '12px', color: '#64748b' }}>
                        {sub.college_eiin}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <button style={styles.btnEdit} onClick={() => handleOpenEdit(sub)}>
                          Edit
                        </button>
                        <button style={styles.btnDelete} onClick={() => handleDelete(sub.id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div style={styles.modalPanel} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.2rem', fontWeight: 800 }}>
              {editingRow ? 'Edit Subject' : 'Add New Subject'}
            </h3>

            <form onSubmit={handleSave}>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Student Group</label>
                <select
                  style={styles.input}
                  value={formData.student_group}
                  onChange={e => setFormData({ ...formData, student_group: e.target.value })}
                >
                  <option value="Science">Science</option>
                  <option value="Humanities">Humanities</option>
                  <option value="Business Studies">Business Studies</option>
                  <option value="General">General</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={styles.label}>Subject Name</label>
                <input
                  type="text"
                  placeholder="e.g. Physics 1st Paper, Civics, Accounting..."
                  style={styles.input}
                  value={formData.subject_name}
                  onChange={e => setFormData({ ...formData, subject_name: e.target.value })}
                  required
                />
              </div>

              {errorMsg && <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '14px' }}>{errorMsg}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" style={styles.btnCancel} onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" style={styles.btnSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Subject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'Outfit', 'Inter', system-ui, sans-serif",
    background: '#f8fafc',
    minHeight: '100vh',
    color: '#0f172a'
  },
  header: {
    background: '#ffffff',
    padding: '16px 32px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 50
  },
  btnBack: {
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    color: '#475569',
    padding: '6px 14px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '13px'
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 900,
    color: '#0f172a'
  },
  subtitle: {
    margin: 0,
    fontSize: '11px',
    color: '#ff5500',
    fontWeight: 800
  },
  btnAdd: {
    background: 'linear-gradient(135deg, #ff7a00, #ff5500)',
    color: '#ffffff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '12px',
    fontWeight: 800,
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(255, 85, 0, 0.3)'
  },
  main: {
    maxWidth: '1000px',
    margin: '32px auto',
    padding: '0 20px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    padding: '12px 14px',
    textAlign: 'left',
    background: '#f1f5f9',
    color: '#475569',
    fontWeight: 800,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  td: {
    padding: '12px 14px',
    verticalAlign: 'middle'
  },
  badgeGroup: {
    padding: '4px 10px',
    borderRadius: '999px',
    background: 'rgba(255, 85, 0, 0.1)',
    color: '#ff5500',
    fontWeight: 800,
    fontSize: '12px'
  },
  btnEdit: {
    padding: '5px 12px',
    borderRadius: '6px',
    border: '1px solid #0366d6',
    background: '#0366d6',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    marginRight: '6px'
  },
  btnDelete: {
    padding: '5px 12px',
    borderRadius: '6px',
    border: '1px solid #ef4444',
    background: '#ef4444',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  errorBox: {
    padding: '12px 16px',
    background: '#fee2e2',
    color: '#dc2626',
    borderRadius: '12px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: 600
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100
  },
  modalPanel: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '28px',
    width: '460px',
    maxWidth: '90%',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 700,
    color: '#475569'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none'
  },
  btnCancel: {
    padding: '9px 18px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    background: '#f8fafc',
    color: '#475569',
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer'
  },
  btnSave: {
    padding: '9px 22px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #ff7a00, #ff5500)',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '13px',
    cursor: 'pointer'
  }
}
